// Evidence-record form templates, one per evidence KIND — the same forms the
// Mindshine SOC 2 Audit Evidence Package ships as sample documents. The
// Record-evidence modal uses these to pre-shape the form for a control:
// `fields` become label/value inputs, `section` becomes an editable table.
// A new evidence kind = a new entry here; storage, rendering, and DOCX export
// are all generic over the same form JSON.

export const EVIDENCE_KINDS = {
  access_review: {
    label: "User access review",
    fields: [
      { key: "Control Mapping", hint: "IAM-06 / CC6.2" },
      { key: "Review Date", hint: "YYYY-MM-DD" },
      { key: "Reviewer", hint: "name (role)" },
      { key: "Certification", hint: "Access reviewed as of <date>; exceptions documented and remediated or accepted." },
      { key: "Exceptions", hint: "None, or list each with its ticket" },
    ],
    section: { title: "Access review", columns: ["User / Account", "Role", "Systems", "Privileged?", "Manager/Owner", "Decision", "Action / Ticket"] },
  },
  jml: {
    label: "Joiner / mover / leaver",
    fields: [
      { key: "Control Mapping", hint: "HR-03 / HR-04 / CC6.3" },
      { key: "Employee", hint: "name — role" },
      { key: "Event", hint: "hire / role change / termination + date" },
      { key: "Owner", hint: "HR / IT owner" },
    ],
    section: { title: "Checklist", columns: ["Item", "Status", "Date", "Evidence"] },
  },
  vuln_remediation: {
    label: "Vulnerability remediation",
    fields: [
      { key: "Control Mapping", hint: "OPS-06 / CC7.1" },
      { key: "Scanner", hint: "what scans and how often" },
      { key: "SLA", hint: "critical 48h / high 7d / medium 30d" },
      { key: "Owner", hint: "name (role)" },
    ],
    section: { title: "Findings & remediation", columns: ["Finding", "Severity", "Opened", "Closed", "Status"] },
  },
  change_release: {
    label: "Change & release",
    fields: [
      { key: "Control Mapping", hint: "CHG-01..05 / CC8.1" },
      { key: "Population", hint: "N production changes in the period" },
      { key: "Peer review", hint: "how changes are reviewed" },
      { key: "CI gate", hint: "what must pass before deploy" },
    ],
    section: { title: "Sample changes", columns: ["Change", "Author", "Reviewer", "CI", "Deployed"] },
  },
  backup_restore: {
    label: "Backup restore / DR test",
    fields: [
      { key: "Control Mapping", hint: "BCP-02..05 / A1.2" },
      { key: "Test Date", hint: "YYYY-MM-DD" },
      { key: "System", hint: "what was restored" },
      { key: "Scenario", hint: "restore <db/service> from <snapshot> into a clean environment" },
      { key: "Approved RTO", hint: "8h" },
      { key: "Approved RPO", hint: "24h" },
      { key: "Actual RTO", hint: "measured time to service" },
      { key: "Actual RPO", hint: "age of the restored backup" },
      { key: "Result", hint: "Pass / Fail" },
      { key: "Operator", hint: "who ran the drill" },
    ],
    section: { title: "Validation", columns: ["Check", "Result", "Evidence"] },
  },
  incident: {
    label: "Incident / IR exercise",
    fields: [
      { key: "Control Mapping", hint: "IR-02..05 / CC7.3-7.5" },
      { key: "Severity", hint: "low / medium / high (or 'tabletop')" },
      { key: "Detected", hint: "when + how" },
      { key: "Contained", hint: "when" },
      { key: "Resolved", hint: "when" },
      { key: "Root cause", hint: "what happened and why" },
      { key: "Customer impact", hint: "none / describe" },
      { key: "Owner", hint: "incident commander" },
    ],
    section: { title: "Timeline", columns: ["Time", "Event"] },
  },
  training: {
    label: "Security training",
    fields: [
      { key: "Control Mapping", hint: "HR-02 / CC1.4" },
      { key: "Campaign", hint: "annual all-hands / at-hire for <name>" },
      { key: "Completion", hint: "N/M staff completed" },
      { key: "Owner", hint: "HR owner" },
    ],
    section: { title: "Completion", columns: ["Employee", "Role", "Completed"] },
  },
  vendor_review: {
    label: "Vendor security review",
    fields: [
      { key: "Control Mapping", hint: "VND-01..04 / CC9.2" },
      { key: "Reviewer", hint: "name (role)" },
      { key: "Cycle", hint: "annual / at onboarding" },
    ],
    section: { title: "Vendors", columns: ["Vendor", "Data", "Assurance reviewed", "Risk", "Action"] },
  },
  risk_register: {
    label: "Risk assessment",
    fields: [
      { key: "Control Mapping", hint: "RISK-01..03 / CC3.1" },
      { key: "Approved by", hint: "name (role)" },
      { key: "Method", hint: "likelihood x impact, quarterly revisit" },
    ],
    section: { title: "Top risks", columns: ["Risk", "L", "I", "Mitigation", "Residual action"] },
  },
  mgmt_review: {
    label: "Management review",
    fields: [
      { key: "Control Mapping", hint: "MGMT-01 / MON-01 / CC4.1" },
      { key: "Attendees", hint: "who attended" },
      { key: "Notes", hint: "what was reviewed and decided" },
      { key: "Sign-off", hint: "recorded" },
    ],
    section: null,
  },
  cloud_config: {
    label: "Cloud configuration review",
    fields: [
      { key: "Control Mapping", hint: "CFG-01..03 / CC7.1" },
      { key: "Method", hint: "CIS benchmark scan + manual review" },
      { key: "Reviewer", hint: "name (role)" },
      { key: "Exceptions", hint: "None, or list with tickets/targets" },
    ],
    section: null,
  },
  generic: {
    label: "Evidence record",
    fields: [
      { key: "Control Mapping", hint: "<control id> / <TSC>" },
      { key: "Activity", hint: "what was performed" },
      { key: "Owner", hint: "name (role)" },
    ],
    section: { title: "Details", columns: ["Item", "Value"] },
  },
};

// control id -> the evidence kind its records usually take.
const PREFIX_KIND = [
  [/^IAM-06/, "access_review"],
  [/^IAM/, "access_review"],
  [/^HR-02/, "training"],
  [/^HR/, "jml"],
  [/^OPS/, "vuln_remediation"],
  [/^MON/, "mgmt_review"],
  [/^MGMT/, "mgmt_review"],
  [/^CHG|^SDLC/, "change_release"],
  [/^BCP/, "backup_restore"],
  [/^IR/, "incident"],
  [/^VND/, "vendor_review"],
  [/^RISK/, "risk_register"],
  [/^CFG|^PHY/, "cloud_config"],
  [/^DATA|^GOV/, "generic"],
];

export function kindForControl(controlId) {
  for (const [re, kind] of PREFIX_KIND) if (re.test(controlId || "")) return kind;
  return "generic";
}

// "Quarterly" / "Annual" / "Monthly" / "At hire + annual" -> period in days;
// null = event-driven or continuous (no calendar schedule to be overdue on).
export function frequencyDays(frequency) {
  const f = String(frequency || "").toLowerCase();
  if (/quarter/.test(f)) return 92;
  if (/month/.test(f)) return 31;
  if (/week/.test(f)) return 7;
  if (/annual|year/.test(f)) return 366;
  return null; // continuous / per event / per change / per hire ...
}

// Overdue = the control has a calendar frequency and its newest evidence is
// missing or older than one period.
export function isOverdue(control, now = new Date()) {
  const days = frequencyDays(control?.frequency);
  if (!days) return false;
  if (!control?.last_evidence_at) return true;
  return (now - new Date(control.last_evidence_at)) / 86_400_000 > days;
}
